import { getQueryClient } from '@/shared/lib/query-client';
import {
  deferAccountScopedQueryReset,
  runAccountSwitchStartCommitLifecycle,
  runAccountSwitchStartRequestLifecycle,
  runClearSwitchSessionLifecycle,
  shouldApplyRemoteSwitchEnd,
} from '@/shared/lib/account-switch-session-policy';
import {
  createTokenCookie,
  deleteCookie,
  getCookie,
} from '@/shared/server-actions';
import {
  beginAccountSwitchTransition,
  clearAccountSwitchTransition,
  getPendingAccountSwitchFingerprint,
} from '@/shared/lib/account-switch-transition';
import { useSpaceStore } from '@/shared/store/space-store';
import { useTokenStore } from '@/shared/store/token-store';
import { getTokenSessionFingerprint } from '@/shared/utils/access-token';

const ACCOUNT_SWITCH_SESSION_LOCK = 'waka-account-switch-session';
const ACCOUNT_SWITCH_SESSION_CHANNEL = 'waka-account-switch-session';
const MAX_SEEN_MESSAGE_IDS = 100;
const REMOTE_START_RETRY_DELAYS_MS = [250, 1000, 3000] as const;

interface AccountSwitchMessageBase {
  messageId: string;
  sourceId: string;
  sessionFingerprint: string;
}

interface AccountSwitchEndMessage extends AccountSwitchMessageBase {
  type: 'END';
}

interface AccountSwitchStartMessage extends AccountSwitchMessageBase {
  type: 'START';
}

type AccountSwitchMessage = AccountSwitchEndMessage | AccountSwitchStartMessage;

interface ClearSwitchSessionOptions {
  allowStaleLocalAccess?: boolean;
  broadcast?: boolean;
  expectedRefreshToken?: string | null;
  expectedSessionFingerprint?: string;
}

export interface CommitAccountSwitchSessionOptions {
  accessToken: string;
  refreshToken: string;
  isRememberMe: boolean;
}

interface StartAccountSwitchSessionOptions {
  isRememberMe: boolean;
  requestTokenPair: () => Promise<{
    accessToken: string;
    refreshToken: string;
  }>;
}

export interface AccountSwitchSessionSnapshot {
  refreshToken: string | null;
  sessionFingerprint: string | null;
}

interface NavigatorWithLocks {
  locks?: {
    request<T>(name: string, callback: () => Promise<T>): Promise<T>;
  };
}

export interface AccountSwitchSessionLockContext {
  isLocked: boolean;
}

let switchSessionEpoch = 0;
let lifecycleChannel: BroadcastChannel | null = null;
let lifecycleInitialized = false;
let remoteStartHandler: ((sessionFingerprint: string) => Promise<void>) | null =
  null;
let pendingRemoteStartFingerprint: string | null = null;
let remoteStartRetryTimer: ReturnType<typeof setTimeout> | null = null;
const seenMessageIds = new Set<string>();
const sourceId = createMessageId();

export class AccountSwitchLockUnavailableError extends Error {
  constructor() {
    super('Web Locks are required to start an account switch session.');
    this.name = 'AccountSwitchLockUnavailableError';
  }
}

function finishRemoteStartTransition(sessionFingerprint?: string): void {
  if (
    sessionFingerprint !== undefined &&
    pendingRemoteStartFingerprint !== sessionFingerprint
  ) {
    clearAccountSwitchTransition(sessionFingerprint);
    return;
  }

  pendingRemoteStartFingerprint = null;
  if (remoteStartRetryTimer) {
    clearTimeout(remoteStartRetryTimer);
    remoteStartRetryTimer = null;
  }
  clearAccountSwitchTransition(sessionFingerprint);
}

function createMessageId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function rememberMessage(messageId: string): void {
  seenMessageIds.add(messageId);
  if (seenMessageIds.size <= MAX_SEEN_MESSAGE_IDS) return;

  const oldestMessageId = seenMessageIds.values().next().value;
  if (oldestMessageId) seenMessageIds.delete(oldestMessageId);
}

export async function captureAccountSwitchSessionSnapshot(): Promise<AccountSwitchSessionSnapshot> {
  const refreshToken = await getCookie('s_r_t');
  return {
    refreshToken,
    sessionFingerprint:
      getTokenSessionFingerprint(refreshToken) ??
      getTokenSessionFingerprint(useTokenStore.getState().switchAccessToken),
  };
}

/**
 * switch refresh/end의 회전 및 정리를 탭 간에 직렬화한다.
 * Web Locks 미지원 환경에서는 호출부의 epoch/cookie compare-and-set 검사가 안전장치다.
 */
export async function withAccountSwitchSessionLock<T>(
  operation: (context: AccountSwitchSessionLockContext) => Promise<T>,
): Promise<T> {
  if (typeof navigator === 'undefined') return operation({ isLocked: false });

  const locks = (navigator as unknown as NavigatorWithLocks).locks;
  if (!locks?.request) return operation({ isLocked: false });

  let operationStarted = false;
  try {
    return await locks.request(ACCOUNT_SWITCH_SESSION_LOCK, async () => {
      operationStarted = true;
      return operation({ isLocked: true });
    });
  } catch (error) {
    // Web Locks 자체가 거부된 경우에만 fallback한다. operation 실패를 재실행하면 안 된다.
    if (!operationStarted) return operation({ isLocked: false });
    throw error;
  }
}

export function getAccountSwitchSessionEpoch(): number {
  return switchSessionEpoch;
}

function invalidateLocalSwitchForRemoteStart(): void {
  switchSessionEpoch += 1;
  useTokenStore.getState().clearSwitchAccount();
  useSpaceStore.getState().clearSpaceId();
  getQueryClient().removeQueries({ type: 'inactive' });
  scheduleAccountScopedQueryTransition(false);
}

function getSwitchRefreshTokenExpiry(isRememberMe: boolean): number {
  return isRememberMe ? 60 * 60 * 24 * 30 : 60 * 60 * 24 * 14;
}

async function resetAccountScopedQueriesNow(): Promise<void> {
  const queryClient = getQueryClient();
  await queryClient.cancelQueries();
  queryClient.removeQueries({ type: 'inactive' });
  try {
    await queryClient.resetQueries({ type: 'active' });
  } finally {
    queryClient.removeQueries({ type: 'inactive' });
  }
}

async function cancelAccountScopedQueriesNow(): Promise<void> {
  const queryClient = getQueryClient();
  await queryClient.cancelQueries();
  queryClient.removeQueries({ type: 'inactive' });
}

function scheduleAccountScopedQueryTransition(resetActive: boolean): void {
  deferAccountScopedQueryReset({
    // active refetch의 401이 같은 non-reentrant Web Lock을 재획득할 수 있으므로
    // credential/cookie atomic callback이 끝난 다음 macrotask로 실행한다.
    schedule: (callback) => {
      setTimeout(callback, 0);
    },
    resetQueries: resetActive
      ? resetAccountScopedQueriesNow
      : cancelAccountScopedQueriesNow,
    reportError: (error) => {
      console.error(
        '[account-switch-session] deferred query reset failed:',
        error,
      );
    },
  });
}

export async function adoptAccountSwitchSessionLocally(
  accessToken: string,
): Promise<void> {
  const queryClient = getQueryClient();
  switchSessionEpoch += 1;
  useTokenStore.getState().clearSwitchAccount();
  useSpaceStore.getState().clearSpaceId();
  useTokenStore.getState().setSwitchAccessToken(accessToken);
  const sessionFingerprint = getTokenSessionFingerprint(accessToken);
  if (sessionFingerprint) {
    finishRemoteStartTransition(sessionFingerprint);
  }
  queryClient.removeQueries({ type: 'inactive' });
  scheduleAccountScopedQueryTransition(true);
}

function broadcastAccountSwitchStart(accessToken: string): void {
  const sessionFingerprint = getTokenSessionFingerprint(accessToken);
  if (!sessionFingerprint) return;

  const message: AccountSwitchStartMessage = {
    type: 'START',
    messageId: createMessageId(),
    sourceId,
    sessionFingerprint,
  };
  rememberMessage(message.messageId);
  getAccountSwitchSessionChannel()?.postMessage(message);
}

async function commitAccountSwitchSessionWithinLock({
  accessToken,
  refreshToken,
  isRememberMe,
}: CommitAccountSwitchSessionOptions): Promise<void> {
  const accessFingerprint = getTokenSessionFingerprint(accessToken);
  const refreshFingerprint = getTokenSessionFingerprint(refreshToken);
  if (
    accessFingerprint &&
    refreshFingerprint &&
    accessFingerprint !== refreshFingerprint
  ) {
    throw new Error('Account switch token pair has different sessions.');
  }

  const queryClient = getQueryClient();
  const previousRefreshToken = await getCookie('s_r_t');
  const previousSessionFingerprint =
    getTokenSessionFingerprint(previousRefreshToken);

  switchSessionEpoch += 1;
  // cookie server action 대기 중 old target query가 old space로 재개되지 않게 동기 정리한다.
  useTokenStore.getState().clearSwitchAccount();
  useSpaceStore.getState().clearSpaceId();

  const commitEpoch = switchSessionEpoch;
  try {
    await runAccountSwitchStartCommitLifecycle({
      createRefreshCookie: () =>
        createTokenCookie({
          name: 's_r_t',
          value: refreshToken,
          maxAge: getSwitchRefreshTokenExpiry(isRememberMe),
        }),
      canCommitAfterCookieWrite: async () =>
        commitEpoch === switchSessionEpoch &&
        (await getCookie('s_r_t')) === refreshToken,
      exposeSwitchAccessToken: () =>
        useTokenStore.getState().setSwitchAccessToken(accessToken),
      rollbackFailedCommit: async () => {
        if (!previousRefreshToken) return;
        await clearSwitchSession(
          previousSessionFingerprint
            ? { expectedSessionFingerprint: previousSessionFingerprint }
            : { expectedRefreshToken: previousRefreshToken },
        );
      },
      cleanupQueries: async () => {
        queryClient.removeQueries({ type: 'inactive' });
      },
      reportRollbackError: (error) => {
        console.error('[account-switch-session] START rollback failed:', error);
      },
    });

    // no-lock fallback에서 늦게 도착한 old END delete까지 한 번 더 감지한다.
    if ((await getCookie('s_r_t')) !== refreshToken) {
      switchSessionEpoch += 1;
      if (useTokenStore.getState().switchAccessToken === accessToken) {
        useTokenStore.getState().clearSwitchAccount();
        useSpaceStore.getState().clearSpaceId();
      }
      throw new Error('Account switch cookie was removed during START commit.');
    }

    broadcastAccountSwitchStart(accessToken);
    if (accessFingerprint) {
      finishRemoteStartTransition(accessFingerprint);
    }
  } finally {
    // 이 뒤에는 await가 없어 timer callback이 lock 해제 전에 실행될 수 없다.
    queryClient.removeQueries({ type: 'inactive' });
    scheduleAccountScopedQueryTransition(true);
  }
}

/** START commit 단독 호출도 refresh/end와 동일한 lock에 참여한다. */
export async function commitAccountSwitchSession(
  options: CommitAccountSwitchSessionOptions,
): Promise<void> {
  await withAccountSwitchSessionLock(async ({ isLocked }) => {
    if (!isLocked) throw new AccountSwitchLockUnavailableError();
    await commitAccountSwitchSessionWithinLock(options);
  });
}

/** 서버 START 요청과 FE commit 전체를 직렬화하여 응답 역전으로 인한 세션 불일치를 막는다. */
export async function startAccountSwitchSession({
  isRememberMe,
  requestTokenPair,
}: StartAccountSwitchSessionOptions): Promise<void> {
  await runAccountSwitchStartRequestLifecycle({
    withLifecycleLock: (operation) =>
      withAccountSwitchSessionLock(async ({ isLocked }) => {
        if (!isLocked) throw new AccountSwitchLockUnavailableError();
        await operation();
      }),
    requestTokenPair,
    commitTokenPair: ({ accessToken, refreshToken }) =>
      commitAccountSwitchSessionWithinLock({
        accessToken,
        refreshToken,
        isRememberMe,
      }),
  });
}

/**
 * switch 세션 종료의 단일 lifecycle 경계.
 * 진행 중 요청 취소 -> credential/space 정리 -> inactive 제거 -> active reset 순서를 지킨다.
 */
export async function clearSwitchSession(
  options: ClearSwitchSessionOptions = {},
): Promise<boolean> {
  const currentAccessToken = useTokenStore.getState().switchAccessToken;
  const currentAccessFingerprint =
    getTokenSessionFingerprint(currentAccessToken);
  const expectedRefreshFingerprint = getTokenSessionFingerprint(
    options.expectedRefreshToken ?? null,
  );
  const expectedFingerprint =
    options.expectedSessionFingerprint ?? expectedRefreshFingerprint;

  if (
    options.expectedSessionFingerprint &&
    !options.allowStaleLocalAccess &&
    currentAccessFingerprint &&
    currentAccessFingerprint !== options.expectedSessionFingerprint
  ) {
    return false;
  }

  const queryClient = getQueryClient();
  let observedCookieFingerprint: string | null = null;
  const didClear = await runClearSwitchSessionLifecycle({
    // network-capable cancel/reset은 Web Lock callback 밖 macrotask로 미룬다.
    cancelQueries: async () => {},
    shouldDeleteRefreshCookie: async () => {
      // local credential을 내린 뒤에도 cookie는 expected old session일 때만 삭제한다.
      const refreshTokenBeforeCleanup = await getCookie('s_r_t');
      observedCookieFingerprint = getTokenSessionFingerprint(
        refreshTokenBeforeCleanup,
      );
      if (
        options.expectedRefreshToken !== undefined &&
        refreshTokenBeforeCleanup !== options.expectedRefreshToken
      ) {
        return false;
      }
      if (
        expectedFingerprint &&
        observedCookieFingerprint &&
        observedCookieFingerprint !== expectedFingerprint
      ) {
        return false;
      }
      return true;
    },
    clearLocalCredentialAndSpace: () => {
      // stale in-flight refresh가 결과를 다시 쓰지 못하게 먼저 epoch를 올린다.
      switchSessionEpoch += 1;
      useTokenStore.getState().clearSwitchAccount();
      useSpaceStore.getState().clearSpaceId();
    },
    deleteRefreshCookie: () => deleteCookie('s_r_t'),
    removeInactiveQueries: () =>
      queryClient.removeQueries({ type: 'inactive' }),
    resetActiveQueries: async () => {},
    reportCleanupError: (error) => {
      console.error('[account-switch-session] cleanup failed:', error);
    },
  });
  scheduleAccountScopedQueryTransition(didClear);
  if (!didClear) return false;

  const endedSessionFingerprint =
    expectedFingerprint ??
    observedCookieFingerprint ??
    currentAccessFingerprint;
  if (endedSessionFingerprint) {
    finishRemoteStartTransition(endedSessionFingerprint);
  } else {
    finishRemoteStartTransition();
  }
  if (options.broadcast !== false && endedSessionFingerprint) {
    const message: AccountSwitchEndMessage = {
      type: 'END',
      messageId: createMessageId(),
      sourceId,
      sessionFingerprint: endedSessionFingerprint,
    };
    rememberMessage(message.messageId);
    getAccountSwitchSessionChannel()?.postMessage(message);
  }

  return true;
}

function isAccountSwitchMessage(value: unknown): value is AccountSwitchMessage {
  if (!value || typeof value !== 'object') return false;
  const message = value as Partial<AccountSwitchMessage>;
  const hasBaseFields =
    typeof message.messageId === 'string' &&
    typeof message.sourceId === 'string' &&
    typeof message.sessionFingerprint === 'string';
  return hasBaseFields && (message.type === 'END' || message.type === 'START');
}

function getAccountSwitchSessionChannel(): BroadcastChannel | null {
  if (
    typeof window === 'undefined' ||
    typeof BroadcastChannel === 'undefined'
  ) {
    return null;
  }
  if (lifecycleChannel) return lifecycleChannel;

  try {
    lifecycleChannel = new BroadcastChannel(ACCOUNT_SWITCH_SESSION_CHANNEL);
  } catch {
    return null;
  }

  return lifecycleChannel;
}

async function handleRemoteSwitchStart(
  sessionFingerprint: string,
  retryIndex = 0,
): Promise<void> {
  if (getPendingAccountSwitchFingerprint() !== sessionFingerprint) return;

  try {
    const currentCookieFingerprint = getTokenSessionFingerprint(
      await getCookie('s_r_t'),
    );
    if (currentCookieFingerprint !== sessionFingerprint) {
      finishRemoteStartTransition(sessionFingerprint);
      return;
    }

    const currentAccessFingerprint = getTokenSessionFingerprint(
      useTokenStore.getState().switchAccessToken,
    );
    if (currentAccessFingerprint !== sessionFingerprint) {
      // cookie authority를 확인한 직후 old X target을 내린다. transition guard는 이
      // 검증 await 전부터 일반 axios 요청을 막고 있어 operator fallback도 발생하지 않는다.
      invalidateLocalSwitchForRemoteStart();
    }

    if (!remoteStartHandler) {
      pendingRemoteStartFingerprint = sessionFingerprint;
      return;
    }

    await remoteStartHandler(sessionFingerprint);
    const refreshedCookieFingerprint = getTokenSessionFingerprint(
      await getCookie('s_r_t'),
    );
    const refreshedAccessFingerprint = getTokenSessionFingerprint(
      useTokenStore.getState().switchAccessToken,
    );
    if (
      refreshedCookieFingerprint !== sessionFingerprint ||
      refreshedAccessFingerprint !== sessionFingerprint
    ) {
      throw new Error('Remote account switch token did not converge.');
    }
    finishRemoteStartTransition(sessionFingerprint);
  } catch (error) {
    if (getPendingAccountSwitchFingerprint() !== sessionFingerprint) return;

    const retryDelay = REMOTE_START_RETRY_DELAYS_MS[retryIndex];
    if (retryDelay !== undefined) {
      remoteStartRetryTimer = setTimeout(() => {
        remoteStartRetryTimer = null;
        void handleRemoteSwitchStart(sessionFingerprint, retryIndex + 1);
      }, retryDelay);
    }
    // bounded retry가 끝나도 transition guard와 cleared local X는 유지한다.
    // 다음 START/END만 이 fail-closed 상태를 해제할 수 있다.
    console.error('[account-switch-session] START sync failed:', error);
  }
}

export function initializeAccountSwitchSessionLifecycle(
  onRemoteStart?: (sessionFingerprint: string) => Promise<void>,
): void {
  if (onRemoteStart) {
    remoteStartHandler = onRemoteStart;
    if (pendingRemoteStartFingerprint) {
      const pendingFingerprint = pendingRemoteStartFingerprint;
      void handleRemoteSwitchStart(pendingFingerprint);
    }
  }
  if (lifecycleInitialized || typeof window === 'undefined') return;
  lifecycleInitialized = true;

  const channel = getAccountSwitchSessionChannel();
  if (!channel) return;

  channel.onmessage = (event: MessageEvent<unknown>) => {
    const message = event.data;
    if (
      !isAccountSwitchMessage(message) ||
      message.sourceId === sourceId ||
      seenMessageIds.has(message.messageId)
    ) {
      return;
    }
    rememberMessage(message.messageId);

    if (message.type === 'START') {
      // raw bearer는 방송하지 않는다. listener 밖의 callback이 shared HttpOnly cookie로
      // switchTokenRefresh를 실행하고 그 함수가 자체 Web Lock을 획득한다.
      pendingRemoteStartFingerprint = message.sessionFingerprint;
      beginAccountSwitchTransition(message.sessionFingerprint);
      if (remoteStartRetryTimer) {
        clearTimeout(remoteStartRetryTimer);
        remoteStartRetryTimer = null;
      }
      void handleRemoteSwitchStart(message.sessionFingerprint);
      return;
    }

    void withAccountSwitchSessionLock(async () => {
      const currentCookieFingerprint = getTokenSessionFingerprint(
        await getCookie('s_r_t'),
      );
      const currentAccessFingerprint = getTokenSessionFingerprint(
        useTokenStore.getState().switchAccessToken,
      );
      if (
        !shouldApplyRemoteSwitchEnd({
          currentCookieFingerprint,
          currentAccessFingerprint,
          endedSessionFingerprint: message.sessionFingerprint,
        })
      ) {
        return;
      }

      await clearSwitchSession({
        allowStaleLocalAccess:
          !!currentAccessFingerprint &&
          currentAccessFingerprint !== message.sessionFingerprint,
        broadcast: false,
        expectedSessionFingerprint: message.sessionFingerprint,
      });
    }).catch((error) => {
      console.error('[account-switch-session] END sync failed:', error);
    });
  };
}

initializeAccountSwitchSessionLifecycle();
